import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import BackgroundImage from './images/summary-background.jpg';

import {
  gamesQuery,
  scoresStatsQuery,
  scenarioLevelsQuery,
  getGamesSessions
} from './api';

import {
  popupMessagesAccuracy,
  popupMessagesAccuracyItems,
  popupMessagesAccuracyGeneralTexts
} from './data';

import Analysis from '../UI/game/analysis';
import PopupMessages from '../UI/introGameLayout/popupMessages';


import './styles.css';

export default function NormalBalanceAccuracy() {
  const params = useParams();
  const levelNumber = Number(params.levelNumber);

  const navigate = useNavigate();
  
  const { 
    getUser,
    getGuidedPlay,
    getCurrentNormalGameId
  } = useAuth();

  const user = getUser();
  const username = user?.alias_name 
    ? user?.alias_name 
    : user?.first_name 
      ? user?.first_name
      : user?.username
        ? user?.username 
        : '';
  
  const gameId = getCurrentNormalGameId();

  const [scenarioId, setScenarioId] = useState(0);
  const { data: games } = useQuery(gamesQuery()) || {};

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Normal Balance') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  const shouldGuidedPagesBeDisplayed = getGuidedPlay();

  const [data, setData] = useState([]);
  const [rank, setRank] = useState('');
  const [accuracy, setAccuracy] = useState('');
  const [averageTime, setAverageTime] = useState('');
  const [levelsCount, setLevelsCount] = useState(0);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsAccuracy'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSession = gamesSessions.find(session => session.user === user?.id && session.game === gameId && !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
          setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));

  useEffect(() => {
    if(scoresStats && scenarioLevels) {
      let scenarioLevelsCount = scenarioLevels.length;
      setLevelsCount(scenarioLevelsCount);

      let scoresStatsData;
      const levels = scoresStats.levels || [];
      if (!shouldGuidedPagesBeDisplayed && levelNumber === scenarioLevelsCount) {
        const {
          totalUserAccuracy,
          totalUserTime,
          totalAccuracy,
          totalMedianAccuracy,
          totalShortestTime,
          totalMedianTime,
          totalLongestTime,
        } = levels.reduce(
          (acc, stat) => {
            acc.totalUserAccuracy += stat.user_accuracy;
            acc.totalUserTime += stat.user_average_time;
            acc.totalAccuracy += stat.average_accuracy;
            acc.totalMedianAccuracy += stat.median_accuracy;
            acc.totalShortestTime += stat.shortest_average_time;
            acc.totalMedianTime += stat.median_average_time;
            acc.totalLongestTime += stat.longest_average_time;
            return acc;
          },
          {
            totalUserAccuracy: 0,
            totalUserTime: 0,
            totalAccuracy: 0,
            totalMedianAccuracy: 0,
            totalShortestTime: 0,
            totalMedianTime: 0,
            totalLongestTime: 0,
          }
        );

        scoresStatsData = {
          user_accuracy: totalUserAccuracy / scenarioLevelsCount,
          user_average_time: totalUserTime / scenarioLevelsCount,
          average_accuracy: totalAccuracy / scenarioLevelsCount,
          median_accuracy: totalMedianAccuracy / scenarioLevelsCount,
          shortest_average_time: totalShortestTime / scenarioLevelsCount,
          median_average_time: totalMedianTime / scenarioLevelsCount,
          longest_average_time: totalLongestTime / scenarioLevelsCount,
          // Game-level rank is computed server-side and exposed as
          // session.rank_session — no need to re-derive from points.
          rank: scoresStats.session?.rank_session,
        };
      } else {
        scoresStatsData = levels.find(scoresStat => scoresStat.level === levelNumber);
      }

      setRank(scoresStatsData?.rank || 'Newbie');
      setAccuracy(`${parseFloat((scoresStatsData?.user_accuracy* 100  || 0 ).toFixed(2)).toString()}%`);
      setAverageTime(parseFloat(scoresStatsData?.user_average_time?.toFixed(2) || 0).toString());

      const firstSectionData = [
        { title: username, score: scoresStatsData?.user_accuracy * 100 || 0, color: '#F9C151' },
        { title: 'Mean', score: scoresStatsData?.average_accuracy * 100 || 0 },
        { title: 'Median', score: scoresStatsData?.median_accuracy * 100 || 0 },
      ];
      const secondSectionData = [
        { title: username, score: scoresStatsData?.user_average_time || 0, color: '#F9C151' },
        { title: 'Mastery', score: 2, color: '#5C91F5' },
        { title: 'Shortest Time', score: scoresStatsData?.shortest_average_time || 0 },
        { title: 'Median', score: scoresStatsData?.median_average_time || 0 },
        { title: 'Longest Time', score: scoresStatsData?.longest_average_time || 0 },
      ];
      const data = [
        {
          title: 'Accuracy',
          bars: firstSectionData,
          barsLabel: '%'
        },
        {
          title: 'Average Time',
          subtitle: 'In Seconds',
          bars: secondSectionData
        }
      ]
      setData(data);
    }
  }, [username, scoresStats, levelNumber, scenarioLevels, shouldGuidedPagesBeDisplayed]);

  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (games && scenarioLevels && scoresStats) {
      setDataLoaded(true);
    }
  }, [games, scenarioLevels, scoresStats]);

  let popupMessages = rank ? popupMessagesAccuracy[rank] : popupMessagesAccuracy['Newbie'];

  const items = popupMessagesAccuracyItems[levelNumber - 1];
  const generalTexts = popupMessagesAccuracyGeneralTexts[levelNumber - 1];

  if(popupMessages) {
    popupMessages = popupMessages.map(message => ({
      ...message,
      text: message.text.replace('{items}', items)
        .replace('{accuracy}', accuracy)
        .replace('{averageTime}', averageTime)
        .replace('{name}', username)
        .replace('{generalTexts}', generalTexts)
    }));
  }

  const handlePopupMessagesNextButtonClick = () => {
    let redirectUrl;
    if(rank === 'Master') {
      if(shouldGuidedPagesBeDisplayed) {
        redirectUrl = `/normal/level/${levelNumber + 1}/intro`;
      } else {
        if(levelNumber === 4) {
          redirectUrl = `/normal/level/5/tutorial`;
        } else {
          redirectUrl = `/normal/level/${levelNumber + 1}/`;
        }
      }
    } else {
      redirectUrl = `/normal/level/${levelNumber}/most-challenging`;
    }

    navigate(redirectUrl);
  }

  const analysisHeading = !shouldGuidedPagesBeDisplayed && levelNumber === levelsCount ? 'Analysis' : `Level ${levelNumber} Analysis`;
  console.log('popupMessages', popupMessages);

  return(
    <Layout
      backgroundImage={BackgroundImage}
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded && popupMessages?.length > 0 &&
          <PopupMessages
            messages={popupMessages}
            buttonText="Next"
            onButtonClick={(levelNumber < levelsCount || rank !== 'Master') ? handlePopupMessagesNextButtonClick : undefined}
            messageSize="small"
          />
      }

      <Analysis
        heading={analysisHeading}
        data={data}
      />
    </Layout>
  );
}
